import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { autorun } from 'mobx';
import { observer } from 'mobx-react';
import * as Sentry from '@sentry/browser';
import { useLocation } from 'react-router-dom';

import { ChatChannelListContext, ChatChannel } from '../../../chat';
import useComponentMounted from '../../../hooks/useComponentMounted';
import FirebaseContext from '../../../context/FirebaseContext';
import useSessionStore from '../../../hooks/useSessionStore';

import { ChannelClaimedFrom } from '../../utils/chatChannel';
import useFeed from '../../hooks/useFeed';
import useToast from '../../hooks/useToast';
import CoachActivity, { activityStatus } from '../../Model/CoachActivity';
import CoachTask from '../../Model/CoachTask';
import ToDo from '../../Model/ToDo';
import ActiveClientsContext from '../ActiveClientsContext';
import CoachConfig, {
  orderType,
} from '../../Model/CoachConfig';
import { usePodFilter } from '../../../context/PodContext';
import feedTypes from './config/feedTypes';
import FeedContext from './FeedContext';
import FeedState from './states';

const FEED_TYPE_QUERY_PARAM = 'type';

// Coach tasks the feed never shows: done, or not the coach's to do yet.
const HIDDEN_COACH_TASK_STATUSES = [activityStatus.COMPLETED, activityStatus.AWAITING_CLIENT_CONSENT];

const queryValueToFeedType = {
  checkin: feedTypes.CHECK_IN,
  workouts: feedTypes.NEED_NEW_WORKOUTS,
  mealplans: feedTypes.NEED_NEW_MEAL_PLAN,
  feedback: feedTypes.WORKOUT_FEEDBACK_REQUEST,
  clientstatus: feedTypes.CLIENT_STATUS,
  dailysummary: feedTypes.DAILY_SUMMARY,
  special: feedTypes.SPECIAL,
  pauserequests: feedTypes.PAUSE_REQUEST,
  archived: feedTypes.ARCHIVED,
  disengaged: feedTypes.DISENGAGED_CLIENT,
  todo: feedTypes.TODO,
};

const FeedContextProvider = ({
  children,
}) => {
  const location = useLocation();

  const [feedActivityList, setFeedActivityList] = useState([]);
  const [initialActivityList, setInitialActivityList] = useState([]);
  const [archivedActivityList, setArchivedActivityList] = useState([]);
  const [initialArchivedActivityList, setInitialArchivedActivityList] = useState([]);
  const [isArchivedActivitiesLoaded, setIsArchivedActivitiesLoaded] = useState(false);

  // Load the default selected feed type from the query parameter
  const [selectedFeedType, setSelectedFeedType] = useState(() => {
    const params = new URLSearchParams(location.search);
    const queryValue = params.get(FEED_TYPE_QUERY_PARAM) || '';
    return queryValueToFeedType[queryValue] || feedTypes.CHECK_IN;
  });

  const [totalUnreadCount, setTotalUnreadCount] = useState({});
  const [feedState, setFeedState] = useState(FeedState.FEED_NOT_INITIALIZED);
  const { firebase: { remote } } = useContext(FirebaseContext);
  const [actionError, setActionError] = useState(false);
  const [activityInProgress, setActivityInProgress] = useState([]);
  const [searchField, setSearchField] = useState();
  const [isOldestToNewest, setIsOldestToNewest] = useState();
  const [selectedActionBarType, setSelectedActionBarType] = useState();
  const [selectedActivity, setSelectedActivity] = useState();
  const [isSmartFeedbackSelected, setIsSmartFeedbackSelected] = useState(false);
  const [coachConfig, setCoachConfig] = useState();
  const [claimedChannelMap, setClaimedChannelMap] = useState(new Map());
  const [showAutomatedItems, setShowAutomatedItems] = useState(false);
  const [openToDoCollection, setOpenToDoCollection] = useState(null);
  const [consolidatedToDoGroups, setConsolidatedToDoGroups] = useState([]);
  const [hasToDoLoadError, setHasToDoLoadError] = useState(false);

  const { claimedChannelCollection, isReady: isChannelListReady } = useContext(ChatChannelListContext);
  const { getFilteredDocs } = usePodFilter();
  const {
    activeClientDocMap,
    isReady: areActiveClientsReady,
    hasError: hasActiveClientsError,
  } = useContext(ActiveClientsContext);

  const {
    userId: authUserId,
    isCoachAssistant,
  } = useSessionStore();

  const {
    userDocForFeed: {
      id: userId,
    },
  } = useFeed();

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  useEffect(() => {
    setFeedState(FeedState.FEED_NOT_INITIALIZED);
    setInitialActivityList([]);
    setSearchField(null);
  }, [
    userId,
  ]);

  // If the disengaged tab was selected via URL but the user can't access it (non-AC), fall back to Check-In.
  useEffect(() => {
    if (selectedFeedType === feedTypes.DISENGAGED_CLIENT && !isCoachAssistant) {
      setSelectedFeedType(feedTypes.CHECK_IN);
    }
  }, [isCoachAssistant, selectedFeedType]);

  useEffect(() => {
    const loadCoachConfig = async () => {
      const coachConfigDoc = await CoachConfig.getCoachConfigByCoachId(userId);
      if (isComponentMountedRef.current) {
        setIsOldestToNewest(!!((coachConfigDoc && coachConfigDoc.activityFeedDefaultOrderValue === orderType.ASC)));
        setCoachConfig(coachConfigDoc);
      }
    };
    loadCoachConfig();
  }, [
    isComponentMountedRef,
    userId,
  ]);

  /**
   * It will make the state of the feed to be erroneous on any error.
   * This will enable a refresh button so that the whole feed can be re-initialized.
   */
  const onFeedError = useCallback(() => {
    setFeedState(FeedState.FEED_ERROR);
  }, []);

  /**
   * It makes the feed to transition again to the NOT_INITIALIZED state so the whole
   * initialization occurs again
   */
  const onFeedRefresh = useCallback(() => {
    setFeedState(FeedState.FEED_NOT_INITIALIZED);
  }, []);

  // The feed is considered to be ready when it's in one of the final states, either initialized or in error state.
  const isFeedReady = useMemo(() => feedState === FeedState.FEED_INITIALIZED || feedState === FeedState.FEED_ERROR, [
    feedState,
  ]);

  /**
 * Whenever a new feedClient gets calculated we subscribe for event for
 * add new activity and deletion of activity from the feed.
 */
  useEffect(() => {
    const loadFeed = async () => {
      if (feedActivityList.length === 0 && !isFeedReady) {
        const activityList = await CoachActivity.getUnarchivedActivitiesForCoach(userId);

        const disposer = autorun(() => {
          const { docs } = activityList;
          if (isComponentMountedRef.current) {
            if (docs.length > 0 && docs[0].user) {
              setInitialActivityList(docs);
            }
            setFeedState(FeedState.FEED_INITIALIZED);
          }
        });

        return disposer;
      }
      return null;
    };

    let disposer = null;
    loadFeed().then((result) => {
      disposer = result;
    });

    return () => {
      if (disposer) {
        disposer();
      }
    };
  }, [
    userId,
    isComponentMountedRef,
    isFeedReady,
    feedActivityList,
  ]);

  useEffect(() => {
    setOpenToDoCollection(null);
    setHasToDoLoadError(false);
    if (!userId) {
      return undefined;
    }

    let cancelled = false;
    const loadToDos = async () => {
      try {
        const toDoCollection = await ToDo.getOpenToDosByCoach(userId);
        if (cancelled || !isComponentMountedRef.current) {
          return;
        }
        setOpenToDoCollection(toDoCollection);
      } catch (err) {
        Sentry.captureException(err, {
          extra: { message: 'Error loading consolidated todos', coach: userId },
        });
        if (!cancelled && isComponentMountedRef.current) {
          setHasToDoLoadError(true);
          showToast('Failed to load to-dos', { error: true });
        }
      }
    };

    loadToDos();

    return () => {
      cancelled = true;
    };
  }, [
    userId,
    isComponentMountedRef,
    showToast,
  ]);

  useEffect(() => {
    if (!openToDoCollection || !areActiveClientsReady) {
      setConsolidatedToDoGroups([]);
      return undefined;
    }

    const disposer = autorun(() => {
      const { docs } = openToDoCollection;
      if (!isComponentMountedRef.current) {
        return;
      }
      const toDosByClient = new Map();
      docs.forEach((toDoDoc) => {
        if (!activeClientDocMap.has(toDoDoc.user)) {
          return;
        }
        if (!toDosByClient.has(toDoDoc.user)) {
          toDosByClient.set(toDoDoc.user, []);
        }
        toDosByClient.get(toDoDoc.user).push(toDoDoc);
      });
      setConsolidatedToDoGroups(Array.from(toDosByClient, ([clientId, toDos]) => ({
        clientId,
        userDoc: activeClientDocMap.get(clientId),
        toDos,
      })));
    });

    return disposer;
  }, [
    openToDoCollection,
    activeClientDocMap,
    areActiveClientsReady,
    isComponentMountedRef,
  ]);

  /**
   * The consolidated to-do feed depends on two independent loads (the to-do query and the shared
   * active-client roster). When either fails the feed has no groups to show, which is the same
   * shape as "this coach has nothing to do" — so surface the failure and let the feed say so.
   */
  const hasConsolidatedToDoError = hasToDoLoadError || hasActiveClientsError;

  /**
 * This will filter the activity list whenever a search keyword is
 * set, sorting added or initial activity list is updated.
 */
  useEffect(() => {
    const disposer = autorun(() => {
      let sortedActivityList = getFilteredDocs([...initialActivityList], 'user');
      let sortedArchivedActivityList = getFilteredDocs([...initialArchivedActivityList], 'user');

      // Coach tasks leave the coach's feed once completed. They stay COACH_TASK docs
      // (so the admin page and mobile history still show them as completed), so we hide
      // rather than archive them. Reading `status` here also makes this autorun react to
      // a completion, removing the card and updating the sidebar count without a refresh.
      // A highlight task still waiting on the client's yes never reaches the coach at all:
      // the feed query only excludes REMOVED, so it is held back here, and appears on its own
      // once the client's app moves it to IN_PROGRESS.
      sortedActivityList = sortedActivityList.filter(
        (activity) => !(activity.type === feedTypes.COACH_TASK && HIDDEN_COACH_TASK_STATUSES.includes(activity.status)),
      );

      if (!isCoachAssistant) {
        // Disengaged clients feed is hidden for non-AC users, so filter them out of both the main
        // and archived activity lists to keep the total unread count consistent with the visible feed tabs.
        sortedActivityList = sortedActivityList
          .filter((activity) => activity.type !== feedTypes.DISENGAGED_CLIENT);
        sortedArchivedActivityList = sortedArchivedActivityList
          .filter((activity) => activity.oldType !== feedTypes.DISENGAGED_CLIENT);
      }
      if (isOldestToNewest) {
        sortedActivityList.reverse();
        sortedArchivedActivityList.reverse();
      }
      // If the AC has selected to hide automated meal plans or workouts, filter out the automated activities
      if (isCoachAssistant && !showAutomatedItems) {
        sortedActivityList = sortedActivityList
          .filter((activity) => (activity.type !== feedTypes.NEED_NEW_MEAL_PLAN || !activity.mealPlanAutomationTime)
            && (activity.type !== feedTypes.NEED_NEW_WORKOUTS || !activity.trackAutomationTime));
      }
      if (searchField) {
        const filteredActivityList = sortedActivityList.filter((activity) => activity.userName === searchField);
        const filteredArchivedActivityList = sortedArchivedActivityList.filter(
          (activity) => activity.userName === searchField,
        );
        setFeedActivityList(filteredActivityList);
        setArchivedActivityList(filteredArchivedActivityList);
      } else {
        setFeedActivityList(sortedActivityList);
        setArchivedActivityList(sortedArchivedActivityList);
      }
    });
    return disposer;
  }, [
    initialActivityList,
    searchField,
    isOldestToNewest,
    initialArchivedActivityList,
    getFilteredDocs,
    isCoachAssistant,
    showAutomatedItems,
  ]);

  /**
   * This will calculate the unread count by type of activity and total
   * unread count whenever the activity feed list updates
   */
  useEffect(() => {
    const unreadCount = { total: 0 };
    const workoutAssignmentIds = [];
    const supportedFeedTypes = Object.values(feedTypes);
    const excludedFeedTypes = [feedTypes.DAILY_SUMMARY, feedTypes.CLIENT_STATUS, feedTypes.ARCHIVED];
    feedActivityList.forEach(({ type, workoutAssignmentId }) => {
      if (!supportedFeedTypes.includes(type) || excludedFeedTypes.includes(type)) {
        return;
      }
      if (!Object.keys(unreadCount).includes(type)) {
        unreadCount[type] = 0;
      }
      if (type === feedTypes.WORKOUT_COMPLETE && workoutAssignmentId) {
        if (!workoutAssignmentIds.includes(workoutAssignmentId)) {
          workoutAssignmentIds.push(workoutAssignmentId);
          unreadCount[type] += 1;
          unreadCount.total += 1;
        }
      } else {
        unreadCount[type] += 1;
        unreadCount.total += 1;
      }
    });

    // Open to-dos only badge their own tab. They persist until someone completes them, so folding
    // them into `total` would leave the sidebar Activity Feed badge with a permanent floor and
    // erode its "needs action" signal.
    const openToDoCount = consolidatedToDoGroups.reduce((total, { toDos }) => total + toDos.length, 0);
    if (openToDoCount > 0) {
      unreadCount[feedTypes.TODO] = openToDoCount;
    }

    if (unreadCount.total !== totalUnreadCount.total
      || unreadCount[feedTypes.TODO] !== totalUnreadCount[feedTypes.TODO]) {
      setTotalUnreadCount({ ...unreadCount });
    }
  }, [
    feedActivityList,
    totalUnreadCount,
    consolidatedToDoGroups,
  ]);

  useEffect(() => {
    const disposer = autorun(() => {
      if (isChannelListReady && !!claimedChannelCollection?.docs && isCoachAssistant) {
        const channelDocMap = new Map();
        claimedChannelCollection.docs.forEach((channelCol) => {
          channelDocMap.set(channelCol.userId, channelCol);
        });
        setClaimedChannelMap(channelDocMap);
      }
    });

    return disposer;
  }, [
    isCoachAssistant,
    claimedChannelCollection,
    isChannelListReady,
  ]);

  const getChannelDoc = useCallback((clientId) => ChatChannel.getUserCoachChannel(clientId, userId), [userId]);

  /**
   * Attempts to claim or reclaim a chat channel for a user.
   *
   * @param {string} user - The client id used to retrieve the associated chat channel document.
   * @param {string} authUserName - The authenticated user's username who is attempting to claim the channel.
   * @param {boolean} reclaim - If true, the function will attempt to reclaim the channel; otherwise, it will claim it.
   */
  const claimChannel = useCallback(async (user, authUserName, reclaim) => {
    const chatChannelDoc = await getChannelDoc(user);

    if (chatChannelDoc) {
      if (reclaim) {
        await chatChannelDoc.reclaim(authUserId, authUserName, ChannelClaimedFrom.CHECKIN);
        return;
      }
      await chatChannelDoc.claim(authUserId, authUserName, ChannelClaimedFrom.CHECKIN);
    }
  }, [getChannelDoc, authUserId]);

  // Attempts to un-claim a chat channel for a user.
  const unclaimChannel = useCallback(async (user) => {
    const chatChannelDoc = await getChannelDoc(user);

    /*
     * Unclaim the chat channel if it's currently claimed by the same user.
     * - authUserId: Id of the user performing the unclaim
     * - {}: No additional fields to update
     * - true: Allows unclaiming if the channel was claimed from a check-in
     */
    if (chatChannelDoc) {
      await chatChannelDoc.unclaimAndUpdateFields(authUserId, {}, true);
    }
  }, [getChannelDoc, authUserId]);

  // handles the functionality for changing between feed types
  const onFeedSelected = useCallback(async (type) => {
    if (isComponentMountedRef.current) {
      setSelectedFeedType(type);
      if (type === feedTypes.ARCHIVED && !isArchivedActivitiesLoaded) {
        setFeedState(FeedState.FEED_NOT_INITIALIZED);
        const activityList = await CoachActivity.getArchivedActivitiesForCoach(userId);
        const { docs } = activityList;
        if (isComponentMountedRef.current) {
          if (docs.length > 0 && docs[0].user) {
            setInitialArchivedActivityList(docs);
            setIsArchivedActivitiesLoaded(true);
          }
          setFeedState(FeedState.FEED_INITIALIZED);
        }
      }
    }
  }, [
    isComponentMountedRef,
    isArchivedActivitiesLoaded,
    userId,
  ]);

  const updateActivity = useCallback(async (activityIds, isArchiveAction) => {
    const normalizedActivityIds = Array.isArray(activityIds) ? activityIds : [activityIds];
    setActivityInProgress([...activityInProgress, ...normalizedActivityIds]);
    const endpoint = `activityFeed-updateActivityStatus/${isArchiveAction ? 'archive' : 'restore'}`;
    try {
      const response = await remote(endpoint, { activityIds: normalizedActivityIds });
      setActivityInProgress([
        ...activityInProgress.filter((activityId) => !normalizedActivityIds.includes(activityId)),
      ]);
      if (!response.ok) {
        setActionError(true);
        return false;
      }
    } catch (err) {
      setActionError(true);
      Sentry.captureException(err, {
        extra: {
          message: 'Error while archiving activity',
          activityIds,
          coach: userId,
        },
      });
      return false;
    }
    return true;
  }, [
    userId,
    remote,
    activityInProgress,
  ]);

  // when coach mark activity as read we archive the activity in the feed.
  // Resolves to whether the archive succeeded; it never rejects.
  const onMarkAsRead = useCallback(async (activity) => {
    const archived = await updateActivity(activity.id, true);
    // Remove the claim from the channel. Not awaited: callers (e.g. sending check-in feedback) don't wait on it.
    unclaimChannel(activity.user).catch((err) => {
      Sentry.captureException(err, {
        extra: { message: 'Error unclaiming channel after marking activity as read', user: activity.user },
      });
    });
    return archived;
  }, [
    updateActivity,
    unclaimChannel,
  ]);

  // Completing a coach task mirrors mobile exactly: stamp status COMPLETED + completedAt
  // + completedBy and nothing else (no archive). The task stays a COACH_TASK doc so it
  // remains visible as completed on the admin page and in mobile history; the coach's
  // feed hides completed coach tasks (the feedActivityList filter above), so the card
  // still leaves their view. `activity.id` is an array of doc ids (tasks are never merged).
  const onMarkTaskComplete = useCallback(async (activity) => {
    const [taskId] = Array.isArray(activity.id) ? activity.id : [activity.id];
    try {
      await new CoachTask(taskId).markComplete(authUserId);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { message: 'Error completing coach task', taskId, coach: userId },
      });
      setActionError(true);
    }
  }, [
    authUserId,
    userId,
    setActionError,
  ]);

  const onMarkAllAsRead = useCallback(async () => {
    const disengagedActivities = feedActivityList.filter((a) => a.type === feedTypes.DISENGAGED_CLIENT);
    const response = await updateActivity(disengagedActivities.map((a) => a.id), true);
    if (response) {
      const results = await Promise.allSettled(disengagedActivities.map((a) => unclaimChannel(a.user)));
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          Sentry.captureException(result.reason, {
            extra: {
              message: 'Error unclaiming channel after bulk archive',
              user: disengagedActivities[index].user,
            },
          });
        }
      });
    }
    return response;
  }, [feedActivityList, updateActivity, unclaimChannel]);

  // when coach restore activity from the deleted activities.
  const onRestore = useCallback(async (activity) => {
    await updateActivity(activity.id, false);
  }, [
    updateActivity,
  ]);

  const uniqueClientNames = useMemo(
    () => [...new Set(getFilteredDocs(initialActivityList, 'user').map((activity) => activity.userName))],
    [getFilteredDocs, initialActivityList],
  );

  const context = useMemo(() => ({
    selectedFeedType,
    onFeedSelected,
    feedState,
    totalUnreadCount,
    onFeedError,
    onFeedRefresh,
    isFeedReady,
    onMarkAsRead,
    onMarkTaskComplete,
    onMarkAllAsRead,
    onRestore,
    feedActivityList,
    actionError,
    setActionError,
    activityInProgress,
    uniqueClientNames,
    setSearchField,
    searchField,
    setIsOldestToNewest,
    isOldestToNewest,
    selectedActionBarType,
    setSelectedActionBarType,
    selectedActivity,
    setSelectedActivity,
    archivedActivityList,
    isSmartFeedbackSelected,
    setIsSmartFeedbackSelected,
    showAutomatedItems,
    setShowAutomatedItems,
    coachConfig,
    setCoachConfig,
    claimedChannelMap,
    claimChannel,
    unclaimChannel,
    consolidatedToDoGroups,
    hasConsolidatedToDoError,
  }), [
    selectedFeedType,
    onFeedSelected,
    feedState,
    totalUnreadCount,
    onFeedError,
    onFeedRefresh,
    isFeedReady,
    onMarkAsRead,
    onMarkTaskComplete,
    onMarkAllAsRead,
    onRestore,
    feedActivityList,
    actionError,
    setActionError,
    activityInProgress,
    setSearchField,
    searchField,
    setIsOldestToNewest,
    isOldestToNewest,
    selectedActionBarType,
    setSelectedActionBarType,
    selectedActivity,
    setSelectedActivity,
    archivedActivityList,
    isSmartFeedbackSelected,
    setIsSmartFeedbackSelected,
    showAutomatedItems,
    setShowAutomatedItems,
    coachConfig,
    setCoachConfig,
    claimedChannelMap,
    claimChannel,
    unclaimChannel,
    uniqueClientNames,
    consolidatedToDoGroups,
    hasConsolidatedToDoError,
  ]);

  return (
    <FeedContext.Provider value={context}>
      {children}
    </FeedContext.Provider>
  );
};

FeedContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(FeedContextProvider);
